import { CodeBlock, SectionTitle, SubTitle, InfoBox } from './shared';

export default function AgentLoopContent() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-3">智能体循环</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          智能体循环是 OpenClaw 处理每条消息的核心流程，涵盖从入口点到最终回复的完整链路。
        </p>
      </div>

      <SectionTitle id="loop-entry">入口点</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        当 Gateway 收到一条新消息时，会触发智能体循环。入口点负责初始化循环上下文，包括加载会话、准备工作区和注入引导文件。
      </p>

      <SectionTitle id="loop-how">工作原理</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">智能体循环的完整流程：</p>
      <div className="space-y-3 my-4">
        {[
          ['消息入队', '新消息进入队列，等待处理'],
          ['会话准备', '加载或创建会话，注入历史消息'],
          ['提示组装', '组合系统提示、记忆、历史消息和当前输入'],
          ['模型调用', '将组装好的提示发送给 AI 模型'],
          ['流式接收', '逐步接收模型输出，实时转发给用户'],
          ['工具执行', '如果模型请求调用工具，执行工具并将结果注入'],
          ['回复整形', '对最终回复进行格式化和过滤'],
          ['会话保存', '将本次对话保存到会话存储'],
        ].map(([title, desc], i) => (
          <div key={title} className="flex items-start gap-3">
            <span className="w-6 h-6 flex items-center justify-center bg-cyan-500/10 text-cyan-400 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
            <div>
              <span className="text-white font-medium text-sm">{title}：</span>
              <span className="text-slate-300 text-sm">{desc}</span>
            </div>
          </div>
        ))}
      </div>

      <SectionTitle id="loop-queue">队列 + 并发</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        每个会话（对等方）有独立的消息队列，同一会话内的消息串行处理，不同会话可以并发处理：
      </p>
      <ul className="space-y-2 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>同一用户的消息按顺序处理，避免并发冲突</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>不同用户的消息可以并发处理，提高吞吐量</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>队列深度可配置，防止消息积压</span></li>
      </ul>
      <CodeBlock language="json5" code={`{
  agent: {
    queue: {
      // 每个会话的最大队列深度
      maxDepth: 10,
      // 队列满时的策略：drop（丢弃）/ wait（等待）
      overflowPolicy: "drop",
      // 最大并发会话数
      maxConcurrentSessions: 100,
    }
  }
}`} />

      <SectionTitle id="loop-session-prep">会话 + 工作区准备</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        每次循环开始时，系统会准备以下内容：
      </p>
      <ul className="space-y-2 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5"><i className="ri-file-list-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>从存储加载当前会话的消息历史</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-file-list-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>读取最新的记忆文件（memory.md）</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-file-list-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>加载所有已启用的技能定义</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-file-list-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>检查上下文窗口是否需要压缩</span></li>
      </ul>

      <SectionTitle id="loop-prompt">提示组装 + 系统提示</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        提示按以下顺序组装：
      </p>
      <CodeBlock language="text" code={`[系统提示]
你是一个有帮助的 AI 助手。

[记忆内容]
## 用户偏好
- 喜欢简洁的回答
- 使用中文

[工具定义]
可用工具：bash, write_file, search_memory...

[历史消息]
用户: 你好
助手: 你好！有什么可以帮助你的？

[当前消息]
用户: 帮我写一个 Python 脚本`} />

      <SectionTitle id="loop-hooks">钩子点</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        智能体循环提供多个钩子点，允许自定义处理逻辑：
      </p>
      <CodeBlock language="typescript" code={`// 在 agent.json5 中配置钩子
{
  agent: {
    hooks: {
      // 消息接收前
      onBeforeMessage: "hooks/before-message.ts",
      // 模型调用前
      onBeforeModel: "hooks/before-model.ts",
      // 工具执行前
      onBeforeTool: "hooks/before-tool.ts",
      // 回复发送前
      onBeforeReply: "hooks/before-reply.ts",
    }
  }
}`} />

      <SectionTitle id="loop-streaming">流式传输 + 部分回复</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        启用流式传输后，模型的输出会被分成多个块实时发送给用户。部分回复机制确保用户能看到实时的生成进度：
      </p>
      <ul className="space-y-2 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>低边界：最小块大小，避免发送过于碎片化的内容</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>高边界：最大块大小，避免单条消息过长</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>类人节奏：块之间添加随机延迟，模拟人类打字速度</span></li>
      </ul>

      <SectionTitle id="loop-tool-exec">工具执行 + 消息工具</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        当模型请求调用工具时，循环会暂停流式传输，执行工具，然后将结果注入到对话中继续生成：
      </p>
      <CodeBlock language="json5" code={`// 工具调用示例（模型输出）
{
  tool_calls: [
    {
      id: "call_abc123",
      type: "function",
      function: {
        name: "bash",
        arguments: { command: "ls -la" }
      }
    }
  ]
}

// 工具结果（注入到对话）
{
  role: "tool",
  tool_call_id: "call_abc123",
  content: "total 48\ndrwxr-xr-x  8 user  staff  256 Jan  1 12:00 ."
}`} />

      <SectionTitle id="loop-reply-shaping">回复整形 + 抑制</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        回复整形阶段对最终输出进行处理：
      </p>
      <ul className="space-y-2 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5"><i className="ri-scissors-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">格式化：</strong>根据渠道特性调整 Markdown 格式</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-scissors-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">抑制：</strong>过滤掉不应发送给用户的内部思考内容</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-scissors-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">截断：</strong>超出渠道消息长度限制时自动截断并分段发送</span></li>
      </ul>

      <SectionTitle id="loop-compaction">压缩 + 重试</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        当上下文窗口接近上限时，系统会自动触发压缩：将历史消息压缩为摘要，释放上下文空间。如果模型调用失败，系统会自动重试（支持指数退避）。
      </p>

      <SectionTitle id="loop-events">事件流</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        智能体循环会发出以下事件，可用于监控和调试：
      </p>
      <CodeBlock language="typescript" code={`// 订阅事件流
agent.on('message:received', (msg) => console.log('收到消息', msg));
agent.on('model:start', (ctx) => console.log('开始调用模型'));
agent.on('tool:call', (tool) => console.log('调用工具', tool.name));
agent.on('reply:sent', (reply) => console.log('回复已发送'));
agent.on('session:saved', (session) => console.log('会话已保存'));`} />

      <SectionTitle id="loop-chat-channel">聊天渠道处理</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        不同渠道的消息处理有细微差异：
      </p>
      <ul className="space-y-2 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5"><i className="ri-chat-1-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">私信：</strong>直接处理，无需群组上下文</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-group-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">群组：</strong>需要 @提及 或特定触发词才响应（可配置）</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-reply-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">线程：</strong>在线程内保持独立的上下文</span></li>
      </ul>

      <SectionTitle id="loop-timeout">超时</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        智能体循环支持多级超时配置：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    timeouts: {
      // 模型调用超时（毫秒）
      modelCall: 60000,
      // 工具执行超时（毫秒）
      toolExecution: 30000,
      // 整个循环超时（毫秒）
      totalLoop: 120000,
    }
  }
}`} />

      <SectionTitle id="loop-early-exit">可能提前结束的情况</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        以下情况会导致智能体循环提前结束：
      </p>
      <ul className="space-y-2 text-sm text-slate-300">
        <li className="flex items-start gap-2.5"><i className="ri-error-warning-line text-amber-400 mt-0.5 flex-shrink-0"></i><span>超出超时限制</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-error-warning-line text-amber-400 mt-0.5 flex-shrink-0"></i><span>模型返回错误（超出重试次数）</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-error-warning-line text-amber-400 mt-0.5 flex-shrink-0"></i><span>钩子函数返回中止信号</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-error-warning-line text-amber-400 mt-0.5 flex-shrink-0"></i><span>工具执行失败且无法恢复</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-error-warning-line text-amber-400 mt-0.5 flex-shrink-0"></i><span>用户发送了中断命令（如 /stop）</span></li>
      </ul>
    </div>
  );
}
